import {
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Radio,
  addToast,
} from "@heroui/react";
import { LimeAlert } from "@/Components/NextBase/LimeAlert";
import { LimeModal } from "@/Components/NextBase/LimeModal";
import { LimeRadioGroup } from "@/Components/NextBase/LimeRadioGroup";
import { Trans } from "@lingui/react/macro";
import { t } from "@lingui/core/macro";
import * as Sentry from "@sentry/react";
import { api } from "@/lib/api-client";
import { useEffect, useState } from "react";
import {
  formatMemberCount,
  isDocumentSharedWithOtherMembers,
} from "./subscriptionInvoiceUtils";

type RemoveAction = "cancel" | "unlink";

type Props = {
  invoiceId: number | null;
  documentNumber: string | null;
  canceled?: boolean;
  /** Whose estimate to withdraw. Null is the subscription holder's. */
  payerCompanyId?: number | null;
  payerName?: string | null;
  documentMemberCount?: number;
  onClose: () => void;
};

export function SubscriptionInvoiceRemoveEstimateModal({
  invoiceId,
  documentNumber,
  canceled = false,
  payerCompanyId = null,
  payerName = null,
  documentMemberCount = 1,
  onClose,
}: Props) {
  const isShared = isDocumentSharedWithOtherMembers({
    memberCount: documentMemberCount,
  });
  const members = formatMemberCount(documentMemberCount);
  const defaultAction: RemoveAction = canceled ? "unlink" : "cancel";
  const [action, setAction] = useState<RemoveAction>(defaultAction);

  useEffect(() => {
    if (invoiceId != null) {
      setAction(canceled ? "unlink" : "cancel");
    }
  }, [invoiceId, canceled]);

  const { mutateAsync: unlinkEstimate, isPending } =
    api.customer.useUnlinkSubscriptionPeriodEstimate();

  const handleClose = () => {
    setAction(defaultAction);
    onClose();
  };

  const handleConfirm = async () => {
    if (invoiceId == null) {
      Sentry.captureMessage(
        "SubscriptionInvoiceRemoveEstimateModal: handleConfirm aborted — missing invoiceId",
        "warning",
      );
      return;
    }
    try {
      await unlinkEstimate({
        invoiceId,
        body: { cancelInTaxRegister: action === "cancel", payerCompanyId },
      });
      addToast({
        title:
          action === "cancel"
            ? t`Predračun je bil preklican in odstranjen`
            : t`Povezava do predračuna je bila odstranjena`,
        color: "success",
      });
      handleClose();
    } catch (error) {
      Sentry.captureException(error);
      addToast({
        title: t`Napaka pri odstranjevanju predračuna`,
        description: t`Prišlo je do napake. Poskusite ponovno.`,
        color: "danger",
      });
    }
  };

  return (
    <LimeModal
      isOpen={invoiceId != null}
      onClose={handleClose}
      data-identifier="subscription-invoice-remove-estimate-modal"
    >
      <ModalContent>
        {() => (
          <>
            <ModalHeader>
              {payerCompanyId != null && payerName ? (
                <Trans>Odstrani predračun podjetja {payerName}</Trans>
              ) : (
                <Trans>Odstrani predračun</Trans>
              )}
            </ModalHeader>
            <ModalBody>
              <div className="flex flex-col gap-3">
                {documentNumber ? (
                  <p className="text-default-700 text-sm">
                    <Trans>
                      Predračun {documentNumber} bo odstranjen iz obdobja.
                    </Trans>
                  </p>
                ) : null}
                {isShared ? (
                  <LimeAlert
                    color="warning"
                    data-identifier="remove-estimate-shared-warning"
                    title={t`Predračun zajema ${members} in bo odstranjen pri vseh.`}
                    description={
                      action === "unlink"
                        ? t`Predračun ostane veljaven v davčni blagajni, obdobja vseh članov pa bo mogoče znova zaračunati.`
                        : undefined
                    }
                  />
                ) : null}
                <LimeRadioGroup
                  size="sm"
                  value={action}
                  onValueChange={(value) => setAction(value as RemoveAction)}
                  data-identifier="remove-estimate-action-radio"
                >
                  <Radio
                    value="cancel"
                    data-identifier="remove-estimate-cancel"
                    isDisabled={canceled}
                    description={
                      canceled ? (
                        <Trans>
                          Predračun je že preklican v davčni blagajni.
                        </Trans>
                      ) : undefined
                    }
                  >
                    <Trans>
                      Tudi izbriši predračun v davčni blagajni (priporočeno)
                    </Trans>
                  </Radio>
                  <Radio
                    value="unlink"
                    data-identifier="remove-estimate-unlink"
                  >
                    <Trans>
                      Samo odstrani povezavo (predračun ostane v davčni
                      blagajni)
                    </Trans>
                  </Radio>
                </LimeRadioGroup>
              </div>
            </ModalBody>
            <ModalFooter>
              <Button variant="light" onPress={handleClose}>
                <Trans>Prekliči</Trans>
              </Button>
              <Button
                color="danger"
                onPress={handleConfirm}
                isLoading={isPending}
                data-identifier="confirm-remove-estimate-button"
              >
                <Trans>Odstrani</Trans>
              </Button>
            </ModalFooter>
          </>
        )}
      </ModalContent>
    </LimeModal>
  );
}
